import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useUserInfo } from "../../context/user";
import { useUsersContext } from "../../context/usersContext";
import { useAvailableIntegrations } from "../../queries/integrations";
import { useAgencyAxisOptions } from "../../queries/agencyAxisOptions";
import { useToolCatalog } from "./hooks/useToolCatalog";
import { resolveAxisWord, resolveForcedAxis, mergeAxisMatch } from "../../lib/onboardingAxes";
import { buildAuthedPath } from "../../lib/buildAuthedPath";
import { resolveTaskById } from "../../lib/taskLookup";
import { recordCampaignArrival } from "../../lib/campaignTelemetry";

/**
 * Resolves an onboarding short link into a redirect to the Task Library with
 * the matching filter applied:
 *  - /app/:slug           — precedence Tool, then Category, then Role, then Sector
 *  - /app/tool/:slug      — forced to the Tool axis only (role/sector/category/:slug likewise)
 * Renders a "No Tasks for X yet" page when the word matches nothing on the
 * relevant axis/axes. Registered after the app's real routes in
 * pages/routes.js, so any reserved route (e.g. /app/calendar, /app/settings)
 * wins automatically via React Router's static-over-dynamic ranking.
 */
export default function OnboardingShortLink({ axis: forcedAxis }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [userInfo, , , , userActiveLocation] = useUserInfo();
  const { tasksData, fetchTasksData } = useUsersContext();
  const { data: availableIntegrationsData } = useAvailableIntegrations(
    userActiveLocation?.id,
    userInfo?.userId,
    userActiveLocation?.agency_id,
  );
  const { categories, roles, sectors, isLoading: axisOptionsLoading } = useAgencyAxisOptions();
  const toolCatalog = useToolCatalog(tasksData?.prompt, availableIntegrationsData?.integrations);

  const [tasksLoaded, setTasksLoaded] = useState(!!tasksData?.prompt);
  useEffect(() => {
    if (tasksData?.prompt) {
      setTasksLoaded(true);
      return;
    }
    fetchTasksData?.()?.finally(() => setTasksLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ready = tasksLoaded && !axisOptionsLoading;

  // `undefined` = still resolving, `null` = resolved with no match, object = matched.
  const match = useMemo(() => {
    if (!ready) return undefined;
    const options = { tools: toolCatalog, categories, roles, sectors };
    return forcedAxis
      ? resolveForcedAxis(forcedAxis, slug, options)
      : resolveAxisWord(slug, options);
  }, [ready, forcedAxis, slug, toolCatalog, categories, roles, sectors]);

  useEffect(() => {
    if (!match) return;
    const search = mergeAxisMatch(location.search, match.axis, match.label);

    const taskIdParam = new URLSearchParams(location.search).get("taskId");
    const taskIdLookup = resolveTaskById(taskIdParam, [tasksData?.prompt || []], ready);
    recordCampaignArrival({
      landingPath: location.pathname,
      search: location.search,
      axis: match.axis,
      label: match.label,
      taskId: taskIdLookup.status === "found" ? taskIdParam : null,
    });

    navigate(`${buildAuthedPath("/app/chat")}?${search}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match]);

  if (match === undefined) return null;
  if (match) return null; // navigating away

  return (
    <div className="flex h-[60vh] flex-col items-center justify-center gap-4 text-center px-4">
      <p className="text-lg font-medium text-foreground">No Tasks for {slug} yet</p>
      <button
        type="button"
        onClick={() => navigate(buildAuthedPath("/app/chat"))}
        className="h-9 px-4 rounded-md bg-foreground text-background text-sm font-medium"
      >
        See all Tasks
      </button>
    </div>
  );
}
